import * as React from "react";
import "./../../components/system/auth/signin/index.css";
import { Button, Input, notification } from "antd";
import { Globe, Lock } from "react-feather";
import { MARKETING_URL } from "../../envVariables";
import { TestimonialHorizontal } from "../../components/system/auth/testimonial-horizontal/testimonial-horizontal";
import { CircularThemeSwitch } from "../../components/CircularThemeSwitch/CircularThemeSwitch";
import { VersionCopyright } from "../../components/generic/version";
import { SupportDocs } from "./supportDocs";
import { useAppSelector } from "../../store/hooks";
import { signInByProvider, signInEmail, signInSaml } from "../../store/userSlice";

interface Props {
  isCustomer: boolean;
}
export const ProviderLogin: React.FC<Props> = ({ isCustomer }) => {
  const theme = useAppSelector((state) => state.theme.theme);

  const [loginType, setLoginType] = React.useState<"saml" | "email" | "provider">("provider");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showSamlProvider, setShowSamlProvider] = React.useState(false);
  const [samlProvider, setSamlProvider] = React.useState("");

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key == "q") {
        if (loginType === "saml") {
          setShowSamlProvider((x) => !x);
        } else {
          setLoginType((v) => (v === "email" ? "provider" : "email"));
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [loginType]);

  async function tryAuthenticate(
    provider: "google" | "microsoft" | "github" | "email" | "saml" | "samlLogin",
    email?: string,
    password?: string,
    samlProvider?: string,
  ) {
    try {
      switch (provider) {
        case "saml":
          await signInSaml(email!, samlProvider!);
          break;
        case "email":
          await signInEmail(email!, password!);
          break;
        case "google":
          await signInByProvider("google");
          break;
        case "github":
          await signInByProvider("github");
          break;
        case "microsoft":
          await signInByProvider("microsoft");
          break;
      }
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({ message: "Failed to Login", description: e.message });
      setLoginType("provider");
    }
  }

  return (
    <>
      <div
        className="signin-page flex text-normal w-full min-h-screen items-stretch p-16 flex-col"
        style={{ backgroundColor: "var(--color-drop)", minWidth: 500, maxWidth: 800 }}
      >
        <div className="w-full flex justify-center mb-4">
          <a href={`${MARKETING_URL}`} target={"_blank"} rel={`noopener`}>
            <img
              className="controlplane-logo"
              height={"auto"}
              src={`/resources/logos/controlPlaneLogo${theme === "dark" ? "White" : ""}.svg`}
              alt="Control Plane Logo"
            />
          </a>
        </div>
        {isCustomer ? (
          <div className="w-full flex justify-center mb-12 mt-2">
            <div className="text-4xl">Sign In to Control Plane</div>
          </div>
        ) : (
          <>
            <div className="w-full flex justify-center mb-4 mt-2">
              <div className="text-4xl">Create a Control Plane Account</div>
            </div>
            <div className="w-full flex justify-center mb-12 mt-2">
              <div className="text-2xl">Access The Enterprise Platform to Run Unbreakable Cloud-Native Apps</div>
            </div>
          </>
        )}
        {loginType !== "provider" ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (loginType === "email") {
                tryAuthenticate("email", email, password);
              }
              if (loginType === "saml") {
                tryAuthenticate("saml", email, undefined, samlProvider);
              }
            }}
            className="flex flex-col items-center w-full"
            data-hs-do-not-collect="true"
          >
            <div className="flex flex-col">
              <div>Email</div>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mb-2" />
              {loginType === "email" ? (
                <>
                  <div>Password</div>
                  <Input value={password} onChange={(e) => setPassword(e.target.value)} className="mb-2" />
                </>
              ) : null}
              {loginType === "saml" && showSamlProvider ? (
                <>
                  <div>SAML Provider</div>
                  <Input value={samlProvider} onChange={(e) => setSamlProvider(e.target.value)} className="mb-2" />
                </>
              ) : null}
              <Button className="mt-4" type={"primary"} color={"blue"}>
                Log In
              </Button>
              <Button className="my-2" onClick={() => setLoginType("provider")} type={"default"}>
                Back
              </Button>
            </div>
          </form>
        ) : (
          <div className="flex flex-col items-center w-full text-sm font-medium" data-hs-do-not-collect="true">
            <button
              onClick={() => tryAuthenticate("google")}
              className={`mb-4 flex items-center pl-4`}
              style={{
                width: 220,
                height: 40,
                backgroundColor: "var(--color-white)",
                boxShadow: `0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%)`,
              }}
            >
              <span>
                <img width={18} height={18} src="/resources/logos/login/google.svg" />
              </span>
              <span className="pl-4 font-medium" style={{ color: "#757575" }}>
                Continue with Google
              </span>
            </button>
            <button
              onClick={() => tryAuthenticate("github")}
              className={`mb-4 flex items-center pl-4`} //
              style={{
                width: 220,
                height: 40,
                boxShadow: `0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%)`,
                backgroundColor: "#333",
                color: "white",
              }}
            >
              <span>
                <img width={18} height={18} src="/resources/logos/login/github.svg" />
              </span>
              <span className="pl-4 font-medium">Continue with Github</span>
            </button>
            <button
              onClick={() => tryAuthenticate("microsoft")}
              className={`mb-4 flex items-center pl-4`} //
              style={{
                width: 220,
                height: 40,
                boxShadow: `0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%)`,
                backgroundColor: "#2f2f2f",
                color: "white",
              }}
            >
              <span>
                <img width={18} height={18} src="/resources/logos/login/microsoft.svg" />
              </span>
              <span className="pl-4">Continue with Microsoft</span>
            </button>
            <button
              onClick={() => setLoginType("saml")}
              className={`mb-4 flex items-center pl-4`} //
              style={{
                width: 220,
                height: 40,
                boxShadow: `0 2px 2px 0 rgb(0 0 0 / 14%), 0 3px 1px -2px rgb(0 0 0 / 20%), 0 1px 5px 0 rgb(0 0 0 / 12%)`,
                backgroundColor: "#2f2f2f",
                color: "white",
              }}
            >
              <span style={{ width: 18, height: 18 }} className="relative">
                <span className="absolute inset-0">
                  <Globe style={{ width: "100%", height: "100%" }} />
                </span>
                <span className="absolute" style={{ top: -4, right: -6 }}>
                  <Lock style={{ width: 10, height: 10 }} />
                </span>
              </span>
              <span className="pl-4">Continue with SAML</span>
            </button>
          </div>
        )}
        <SupportDocs />
        <TestimonialHorizontal
          quote="With Control Plane, we can go from concept to production so much faster. What used to take weeks or months now takes an hour."
          company="CEO at Verdova"
          photo={"/resources/images/maxFeinberg-verdova.jpeg"}
          name={"Max Feinberg"}
        />
        <div className="flex-grow" />
        <div className="mt-16">
          <div className={"flex items-center justify-center mb-2"}>
            <CircularThemeSwitch />
          </div>
          <div className="text-footer text-center">
            <VersionCopyright /> {new Date().getFullYear()} Control Plane. All rights reserved.
          </div>
        </div>
      </div>
    </>
  );
};
